import type { ReactNode } from "react";
import { TalkingOrb } from "./TalkingOrb";
import { ConversationEmptyState } from "./ConversationEmptyState";
import {
  InterviewProgressTimeline,
  type SectionProgress,
} from "./InterviewProgressTimeline";
import {
  LiveControls,
  type ConnectionState,
  type NetworkQualityState,
} from "./LiveControls";
import type { ConversationStatus } from "./ConversationStatus";

export interface InterviewViewProps {
  /** Connection state */
  connectionState: ConnectionState;
  /** Conversation status (idle, listening, thinking) */
  conversationStatus: ConversationStatus;
  /** Whether bot has started speaking */
  botHasStartedSpeaking: boolean;
  /** Bot's audio track for visualization */
  botAudioTrack: MediaStreamTrack | null;
  /** Whether mic is enabled */
  isMicEnabled: boolean;
  /** Section progress for timeline */
  sectionProgress: SectionProgress[];
  /** Current section ID */
  currentSectionId: string | null;
  /** Network quality state */
  networkQualityState?: NetworkQualityState;
  /** Callbacks */
  onToggleMic: () => void;
  onPause: () => void;
  onReconnect?: () => void;
  onMicrophoneChange?: (deviceId: string) => void;
  onSpeakerChange?: (deviceId: string) => void;
  /** Optional children to render (e.g., form carousel) */
  children?: ReactNode;
  /** Use compact layout (for shorter screens) */
  compact?: boolean;
  className?: string;
}

/**
 * Full interview view matching the web app layout
 * Includes Card, TalkingOrb, ConversationEmptyState, ProgressTimeline, and LiveControls
 */
export function InterviewView({
  connectionState,
  conversationStatus,
  botHasStartedSpeaking,
  botAudioTrack,
  isMicEnabled,
  sectionProgress,
  currentSectionId,
  networkQualityState = "unknown",
  onToggleMic,
  onPause,
  onReconnect,
  onMicrophoneChange,
  onSpeakerChange,
  children,
  compact = false,
  className,
}: InterviewViewProps) {
  const isConnected =
    connectionState === "connected" || connectionState === "ready";
  const orbSize = compact ? 120 : 150;

  // Determine empty state text based on connection state
  const getEmptyStateText = () => {
    if (connectionState === "connected" || connectionState === "ready") {
      return {
        title: "Connected",
        description: "Your interviewer is joining",
      };
    }
    return {
      title: "Connecting...",
      description: "We'll get started soon",
    };
  };

  const emptyState = getEmptyStateText();

  return (
    <div
      className={`w-full max-w-2xl mx-auto flex-1 flex flex-col text-foreground min-h-0 ${className || ""}`}
    >
      {/* Header with orb and conversation state */}
      <div className="flex flex-col p-4 pb-0 overflow-visible">
        {/* Talking Orb - Centered at top with padding for glow effect */}
        <div
          className={`flex justify-center items-center w-full ${compact ? "py-4" : "py-8"}`}
        >
          <TalkingOrb
            isActive={isConnected}
            size={orbSize}
            audioTrack={botAudioTrack}
          />
        </div>

        {/* Conversation empty state - show when bot hasn't started speaking */}
        {!botHasStartedSpeaking && (
          <div className="w-full">
            <ConversationEmptyState
              title={emptyState.title}
              description={emptyState.description}
            />
          </div>
        )}
      </div>

      {/* Content with progress, forms, and controls */}
      <div className="p-4 pt-6 flex flex-col gap-4 flex-1 min-h-0">
        {/* Progress Timeline - Above form/controls */}
        {sectionProgress.length > 0 && (
          <div className="flex justify-center w-full">
            <InterviewProgressTimeline
              sections={sectionProgress}
              currentSectionId={currentSectionId}
              className="w-full max-w-md"
            />
          </div>
        )}

        {/* Optional children (e.g., form carousel) */}
        {children && (
          <div className="w-full flex-1 flex flex-col justify-center">
            {children}
          </div>
        )}

        {/* Live Recording Controls */}
        <LiveControls
          connectionState={connectionState}
          conversationStatus={conversationStatus}
          isMicEnabled={isMicEnabled}
          onToggleMic={onToggleMic}
          onPause={onPause}
          onReconnect={onReconnect}
          onMicrophoneChange={onMicrophoneChange}
          onSpeakerChange={onSpeakerChange}
          audioTrack={botAudioTrack}
          networkQualityState={networkQualityState}
        />
      </div>
    </div>
  );
}
